import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../shared/Modal';
import { SelectField } from '../shared/FormField';
import ModuleScopeField, { parseModuleScopeValue } from '../shared/ModuleScopeField';

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to update role assignment.';

function formatRoleLabel(role = {}) {
  const parts = [role.name, role.code].filter(Boolean);
  return parts.length ? parts.join(' - ') : role.uuid;
}

export default function EditRoleAssignmentModal({
  isOpen,
  onClose,
  onSave,
  row,
  institutions = [],
  roles = [],
}) {
  const [form, setForm] = useState({
    role_code:        '',
    institution_uuid: '',
    module_scope:     '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const currentRoleCode = String(row?.role_code ?? row?.role?.code ?? '').trim();
  const isR03 = form.role_code === 'domain_data_steward';
  const currentRoleLabel = row?.role?.name
    ? `${row.role.name}${row.role.code ? ` - ${row.role.code}` : ''}`
    : row?.role_label
    ? String(row.role_label)
    : currentRoleCode;

  useEffect(() => {
    if (!row) return;

    setForm({
      role_code:        row.role_code ?? row.role?.code ?? '',
      institution_uuid: row.institution_uuid ?? row.institution_id ?? '',
      module_scope:     row.module_scope ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const roleOptions = useMemo(
    () => {
      const options = roles
        .slice()
        .sort((a, b) => String(a.name ?? a.code ?? '').localeCompare(String(b.name ?? b.code ?? ''), undefined, { numeric: true, sensitivity: 'base' }))
        .map((role) => ({
          value: role.code,
          label: formatRoleLabel(role),
        }));

      if (currentRoleCode && !options.some((option) => option.value === currentRoleCode)) {
        options.unshift({
          value: currentRoleCode,
          label: currentRoleLabel || currentRoleCode,
        });
      }

      return options;
    },
    [roles, currentRoleCode, currentRoleLabel]
  );

  const institutionOptions = useMemo(
    () => [
      { value: '', label: 'All Institutions' },
      ...institutions.map((institution) => ({
        value: institution.uuid ?? institution.id,
        label: institution.name ?? institution.uuid ?? institution.id ?? 'Unknown institution',
      })),
    ],
    [institutions]
  );

  const submit = async () => {
    if (submitting) return;
    if (!form.role_code) return;
    if (isR03 && parseModuleScopeValue(form.module_scope).length === 0) {
      setErrMsg('R03 assignments need at least one module. Use Module 1 and 2 for this department.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid:             row.uuid,
        user_uuid:        row.user_uuid,
        role_code:        form.role_code,
        institution_uuid: form.institution_uuid || null,
        module_scope:     form.module_scope || null,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Edit Role Assignment"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Role Assignment Updated"
      successMessage="The assignment changes were saved to the backend."
      isSubmitting={submitting}
    >
      <div className="grid gap-2">
        <span className="text-[13px] font-semibold text-gray-900">User</span>
        <div className="rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-500 select-none">
          {row?.user_label || row?.user_uuid || '-'}
        </div>
        <span className="text-[11px] text-gray-400">Assignment owner is read-only.</span>
      </div>
      <SelectField
        label="Role"
        required
        value={form.role_code}
        onChange={set('role_code')}
        options={roleOptions}
        placeholder="Select Role"
      />
      <SelectField
        label="Institution Scope"
        value={form.institution_uuid}
        onChange={set('institution_uuid')}
        options={institutionOptions}
        placeholder="All Institutions"
      />
      <ModuleScopeField
        required={isR03}
        value={form.module_scope}
        onChange={set('module_scope')}
        showShortcut={isR03}
        note="Select one or more modules, or use All Modules to apply the full set at once."
      />
      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600">
        Expiration is calculated by the backend, so there is no expiry input here.
      </div>
    </Modal>
  );
}
